/**
 * useJobDetail 훅
 *
 * 책임:
 * - Job "스냅샷" 통합 조회: /jobs/{id}(상세) + /jobs/{id}/stages(스테이지)를
 *   같은 30초 주기로 함께 폴링해 단일 상태로 반환.
 *   → 상태 배지 · 실행 타임라인 · StageView 3요소가 한 번의 refresh로 동시 갱신.
 * - 원본 스냅샷 URL 조회
 * - Job 재시도 (inputJson 재사용)
 * - 로딩/에러 상태 관리
 *
 * JobDetailContainer에서 사용.
 * status/타임라인은 폴링된 detail에서 직접 오므로 별도 상태 역산이 필요 없다.
 */

import { useEffect, useState, useCallback } from 'react';
import * as endpoints from '../api/endpoints';
import type { JobDetailDto } from '../api/types';
import type { StageColumnGroup, StageRun } from '../organisms/StageView/StageView';
import {
  toStageColumns,
  toStageRun,
  leafNosOf,
  deriveJobStatus,
  isJobSettled,
} from './stageMapping';
import type { StageJobRef } from './stageMapping';

export interface UseJobDetailReturn {
  data: JobDetailDto | null;
  /** 스테이지 뷰 열/행 (같은 폴링의 /jobs/{id}/stages 결과). */
  stageColumns: StageColumnGroup[];
  stageRuns: StageRun[];
  /** 이 Job의 원본 스냅샷(collectionId=스냅샷 id)이 실제로 존재하는지. 버튼 렌더 조건. */
  snapshotAvailable: boolean;
  loading: boolean;
  error: string | null;
  /** 원본 스냅샷 파일 다운로드(바이너리 Blob + 파일명). */
  downloadSnapshot: () => Promise<{ blob: Blob; filename: string }>;
  retryJob: () => Promise<number>; // 새 jobId 반환
  retry: () => void;
}

// 30초 주기 폴링 — 진행 중 Job의 상태/타임라인/스테이지가 실시간 반영되도록.
const POLL_MS = 30000;

export function useJobDetail(
  token: string | null,
  jobId: number | null
): UseJobDetailReturn {
  const [data, setData] = useState<JobDetailDto | null>(null);
  const [stageColumns, setStageColumns] = useState<StageColumnGroup[]>([]);
  const [stageRuns, setStageRuns] = useState<StageRun[]>([]);
  const [snapshotAvailable, setSnapshotAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 수동 재조회 트리거 (증가 시 폴링 effect 재실행).
  const [reloadTick, setReloadTick] = useState(0);

  // 원본 스냅샷 다운로드: 이 Job의 모듈/collectionId(=스냅샷 id)로 파일(Blob)을 받아 반환.
  const downloadSnapshot = useCallback(async () => {
    if (!data?.collectionId) throw new Error('원본 스냅샷이 없습니다');
    return endpoints.downloadSnapshot(data.module, data.collectionId);
  }, [data]);

  // Job 재시도: 기존 Job의 inputJson을 재사용해 새 Job 생성
  const doRetry = useCallback(async () => {
    if (jobId === null) throw new Error('No job ID');
    const result = await endpoints.retryJob(jobId);
    return result.jobId;
  }, [jobId]);

  const retry = useCallback(() => setReloadTick((n) => n + 1), []);

  // 마운트/jobId 변경/수동 재조회 시 초기 조회 + 30초 폴링.
  useEffect(() => {
    if (!token || jobId === null) {
      setData(null);
      setStageColumns([]);
      setStageRuns([]);
      setSnapshotAvailable(false);
      return;
    }
    let alive = true;
    let timer: ReturnType<typeof setInterval> | undefined;
    const stopPolling = () => {
      if (timer) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    // showLoading=true는 최초/키 변경 조회에만. 폴링은 조용히 갱신.
    // 상세는 필수, 스테이지는 실패해도 상세만 표시(best-effort).
    const run = (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      Promise.all([
        endpoints.fetchJobDetail(jobId),
        endpoints.fetchJobStages(jobId).catch(() => null),
      ])
        .then(([detail, stages]) => {
          if (!alive) return;
          setError(null);
          setData(detail);
          // 원본 스냅샷 존재 여부: 이 Job의 collectionId(=스냅샷 id)가 모듈 스냅샷 목록에
          // 실제로 남아 있는지 조회해 확인(없거나 파기됐으면 버튼 숨김).
          if (detail.collectionId) {
            const snapId = detail.collectionId;
            endpoints
              .fetchModuleSnapshots(detail.module)
              .then((res) => {
                if (!alive) return;
                setSnapshotAvailable(res.snapshots.some((s) => s.id === snapId));
              })
              .catch(() => {
                if (alive) setSnapshotAvailable(false);
              });
          } else {
            setSnapshotAvailable(false);
          }
          if (stages) {
            const ref: StageJobRef = {
              id: detail.id,
              module: detail.module,
              status: detail.status,
              created: detail.created,
            };
            const cols = toStageColumns(stages);
            setStageColumns(cols);
            setStageRuns([toStageRun(stages, ref, leafNosOf(cols))]);
          } else {
            setStageColumns([]);
            setStageRuns([]);
          }
          // 이미 완료/실패한 Job은 더 바뀌지 않으므로 타이머를 돌릴 필요가 없다.
          // stages가 있으면 폴링으로 갱신되는 단계 상태 기준, stages를 못 받아도
          // 상세 status가 종결(SUCCESS/FAILED)이면 중단.
          const settled = stages
            ? isJobSettled(deriveJobStatus(stages))
            : isJobSettled(detail.status);
          if (settled) stopPolling();
        })
        .catch((err) => {
          if (!alive) return;
          setError(err instanceof Error ? err.message : 'Failed to load job');
        })
        .finally(() => {
          if (alive && showLoading) setLoading(false);
        });
    };

    run(true);
    timer = setInterval(() => run(false), POLL_MS);
    return () => {
      alive = false;
      stopPolling();
    };
  }, [token, jobId, reloadTick]);

  return {
    data,
    stageColumns,
    stageRuns,
    snapshotAvailable,
    loading,
    error,
    downloadSnapshot,
    retryJob: doRetry,
    retry,
  };
}
